/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TableHead from '@mui/material/TableHead';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import FormQuantityAdd from './FormQuantityAdd';

const initFind = {
  search: '',
  supplier: 'PO',
  round: '7',
  order_by: 'tb.diff_stock',
  order_sort: 'asc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  best: 'ALL',
  condition: 'branch_stock',
};

function SearchMinMax({ add, setButtonGroup }) {
  const mod = 'purchase_order_list';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(false);
  const [modalAdd, setModalAdd] = useState(false);
  const [cboBest, setCboBest] = useState([]);
  const txtSearch = useRef(null);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const view = (data) => {
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('OPTIONS', `/${mod}/minmax`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      supplier: data.supplier,
      round: data.round,
      best: data.best,
      condition: data.condition,
      ref_id: id,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const addCallback = (item, quantity) => {
    add({
      barcode_id: item.barcode_id,
      quantity: quantity,
      quantity_balance: quantity,
      unit_id: item.unit_id,
    });
  };

  const renderBuy = (row) => {
    if (row.quantity_buy * 1 <= 0) return '-';

    return (
      <MDBox>
        <MDBox>
          <Tooltip title={`สั่งซื้อ ${Func.numberDisplay(row.quantity_buy)} ${row.unit_name}`}>
            <MDButton
              variant='text'
              color='success'
              size='small'
              sx={{
                p: 0,
                m: 0,
                fontSize: '1rem',
                display: 'inline-block',
                textAlign: 'right',
                minHeight: 'unset',
                fontWeight: 'normal',
              }}
              onClick={() => addCallback(row, row.quantity_buy)}
            >
              <Icon>add_circle_outline</Icon> {Func.numberDisplay(row.quantity_buy)}
            </MDButton>
          </Tooltip>
        </MDBox>
        <MDBox>{row.unit_name}</MDBox>
      </MDBox>
    );
  };

  const renderSearch = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  inputRef: txtSearch,
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.best}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, best: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>Rating :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='1'>A</MenuItem>
              <MenuItem value='2'>B</MenuItem>
              <MenuItem value='3'>C</MenuItem>
              <MenuItem value='4'>D</MenuItem>
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.condition}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, condition: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>เงื่อนไข :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='branch'>หน้าร้านไม่เพียงพอ</MenuItem>
              <MenuItem value='stock'>STO ไม่เพียงพอ</MenuItem>
              <MenuItem value='branch_stock'>ถึงจุดสั่งซื้อ และโอนย้าย</MenuItem>
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.supplier}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, supplier: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>ผู้จัดจำหน่าย :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='PO'>ตามใบสั่งซื้อ</MenuItem>
            </MDInput>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const balanceStock = (row) => {
    if (row.balance_stock > 0) {
      return <MDBox color='error'>{Func.numberDisplay(row.balance_stock)}</MDBox>;
    }

    return '-';
  };

  const balanceBranch = (row) => {
    if (row.balance_branch > 0) {
      return <MDBox color='error'>{Func.numberDisplay(row.balance_branch)}</MDBox>;
    }

    return '-';
  };

  const balanceAll = (row) => {
    if (row.balance_all > 0) {
      return <MDBox color='primary'>{Func.numberDisplay(row.balance_all)}</MDBox>;
    }

    return '-';
  };

  const diffStockDisplay = (row) => {
    const diff = row.diff_stock * 1;

    if (diff <= 0) {
      return <MDBox color='error'>{Func.numberDisplay(row.purchase_stock)}</MDBox>;
    }

    return <MDBox>{Func.numberDisplay(row.purchase_stock)}</MDBox>;
  };

  const diffBranchDisplay = (row) => {
    const diff = row.diff_branch * 1;

    if (diff <= 0) {
      return <MDBox color='error'>{Func.numberDisplay(row.purchase_branch)}</MDBox>;
    }

    return <MDBox>{Func.numberDisplay(row.purchase_branch)}</MDBox>;
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 0, minHeight: 500 }}>
              <Table aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell className='nowrap' rowSpan={2}>
                      รูป
                    </TableCell>
                    <TableCell className='nowrap' rowSpan={2}>
                      สินค้า
                    </TableCell>
                    <TableCell className='nowrap' rowSpan={2}>
                      ผู้จัดจำหน่าย
                    </TableCell>
                    <TableCell className='nowrap' align='center' rowSpan={2}>
                      Rating
                    </TableCell>
                    <TableCell className='nowrap' align='right' rowSpan={2}>
                      ซื้อ
                      <br />
                      ล่าสุด
                    </TableCell>
                    <TableCell className='nowrap' align='right' rowSpan={2}>
                      ขายเฉลี่ย
                      <br />
                      ต่อวัน
                    </TableCell>
                    <TableCell className='nowrap highlight-warning' align='center' colSpan={3}>
                      หน้าร้าน
                    </TableCell>
                    <TableCell className='nowrap highlight-info' align='center' colSpan={4}>
                      STO
                    </TableCell>
                    <TableCell className='nowrap highlight-success' align='center' colSpan={5}>
                      สั่งซื้อ
                    </TableCell>
                    <TableCell className='nowrap' align='center' width={150} rowSpan={2}>
                      จำนวนต้องการ
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className='nowrap highlight-warning' align='right'>
                      จุดเติม
                    </TableCell>
                    <TableCell className='nowrap highlight-warning' align='right'>
                      คลัง
                    </TableCell>
                    <TableCell className='nowrap highlight-warning' align='right'>
                      คงเหลือ
                    </TableCell>
                    <TableCell className='nowrap highlight-info' align='right'>
                      จุดสั่ง
                    </TableCell>
                    <TableCell className='nowrap highlight-info' align='right'>
                      เติมเต็ม
                    </TableCell>
                    <TableCell className='nowrap highlight-info' align='right'>
                      คลัง
                    </TableCell>
                    <TableCell className='nowrap highlight-info' align='right'>
                      คงเหลือ
                    </TableCell>
                    <TableCell className='nowrap highlight-success' align='right'>
                      ALL
                    </TableCell>
                    <TableCell className='nowrap highlight-success' align='right'>
                      หน้าร้าน
                    </TableCell>
                    <TableCell className='nowrap highlight-success' align='right'>
                      STO
                    </TableCell>
                    <TableCell className='nowrap highlight-success' align='right'>
                      สุทธิ
                    </TableCell>
                    <TableCell className='nowrap highlight-success' align='right'>
                      ซื้อแล้ว
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{Func.imageDisplay(row)}</TableCell>
                      <TableCell>
                        <MDBox>{row.code}</MDBox>
                        <MDBox className='cut' sx={{ width: 120 }}>
                          {row.name}
                        </MDBox>
                      </TableCell>
                      <TableCell>
                        <MDBox className='nowrap'>{row.supplier_code}</MDBox>
                        <MDBox className='nowrap'>
                          {Func.numberDisplay(row.lead_time)} +{' '}
                          {Func.numberDisplay(row.review_period)}
                        </MDBox>
                      </TableCell>
                      <TableCell align='center'>
                        {row.best_name === '' ? (
                          'N/A'
                        ) : (
                          <MDBox>
                            <MDBox>
                              <code>{row.best_name}</code>
                            </MDBox>
                            <MDBox>{Func.numberDisplay(row.percent_purchase)}%</MDBox>
                          </MDBox>
                        )}
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(row.cost, 2)}</MDBox>
                        <MDBox>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(row.quantity_avg)}</MDBox>
                        <MDBox className='nowrap'>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox color='info'>{Func.numberDisplay(row.day_export)}</MDBox>
                        <MDBox>วัน</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(row.quantity_balance)}</MDBox>
                        <MDBox className='nowrap'>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right' className='highlight-warning'>
                        <MDBox>{diffBranchDisplay(row)}</MDBox>
                        <MDBox>วัน</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox color='info'>{Func.numberDisplay(row.day_purchase)}</MDBox>
                        <MDBox>วัน</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox color='info'>{Func.numberDisplay(row.purchase_standard)}</MDBox>
                        <MDBox>วัน</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(row.quantity_stock)}</MDBox>
                        <MDBox className='nowrap'>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right' className='highlight-info'>
                        <MDBox>{diffStockDisplay(row)}</MDBox>
                        <MDBox>วัน</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='color-orange'>
                          {Func.numberDisplay(row.quantity_point)}
                        </MDBox>
                        <MDBox className='nowrap'>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{balanceBranch(row)}</MDBox>
                        <MDBox className='nowrap'>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{balanceStock(row)}</MDBox>
                        <MDBox className='nowrap'>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right' className='highlight-danger'>
                        <MDBox>{balanceAll(row)}</MDBox>
                        <MDBox className='nowrap'>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right' className='highlight-success'>
                        <MDBox>{Func.numberDisplay(row.quantity_purchase)}</MDBox>
                        <MDBox className='nowrap'>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='center'>
                        <FormQuantityAdd parent={row} callback={addCallback} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
              <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                {t(`element.show_record`, { num: find.perPage })}&nbsp;
                <Icon>keyboard_arrow_down</Icon>
              </MDButton>
              {renderShow}
              <Pagination
                count={pageTotal}
                variant='outlined'
                color='secondary'
                showFirstButton
                showLastButton
                page={pageNo}
                onChange={(e, p) => view({ ...find, page: p })}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog
        open={modalAdd}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={() => setModalAdd(false)}
      >
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderList()}</DialogContent>
      </Dialog>
    );
  };

  return (
    <MDBox>
      <MenuItem
        onClick={() => {
          setButtonGroup(null);
          setModalAdd(true);
          view(find);

          setTimeout(function () {
            txtSearch.current.focus();
          }, 500);
        }}
      >
        <MDTypography variant='body2'>
          <Icon color='info'>add_circle_outline</Icon>&nbsp;จุดสั่งซื้อ
        </MDTypography>
      </MenuItem>

      {renderAdd()}
    </MDBox>
  );
}

SearchMinMax.propTypes = {
  add: PropTypes.func.isRequired,
  setButtonGroup: PropTypes.func.isRequired,
};

export default SearchMinMax;
